import { Fragment, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  alpha,
  Collapse,
  IconButton,
  TableCell as MaterialTableCell,
  TableRow as MaterialTableRow
} from '@mui/material';

import { RadioButton, TableCell } from 'ui/atoms';
import type { Column, PinnedLayout, Row } from 'ui/organisms';
import palette from 'ui/theme/palette';
import { TableCheckboxCell } from '../TableCheckboxCell/table-checkbox-cell.molecule';

interface Props<T> {
  isRowSelected: boolean;
  selectable: boolean;
  clickable: boolean;
  row: Row<T>;
  columns: Column<T>[];
  pinnedLayout?: PinnedLayout;
  handleCheckboxSelect: (event: React.MouseEvent<unknown>, id: string) => void;
  handleCellClick: (event: React.MouseEvent<unknown>, id: string, data: T) => void;
  handleRadioSelect: (value: string) => void;
  isLastItem: boolean;
  singleSelectable: boolean;
  selected: boolean;
  hasExpandableRows?: boolean;
}

export const TableRow = <T,>(props: Props<T>) => {
  const [open, setOpen] = useState(false);
  const isHighlightedRow = props.row.heavyBottomBorder || Boolean(props.row.highlightBackground);
  let rowBackgroundColor = 'unset';
  if (isHighlightedRow) {
    rowBackgroundColor = props.row.highlightBackground ?? palette.background.default;
  }
  const hasExpansion = Boolean(props.row.expandedContent);
  const totalCols =
    props.columns.length +
    (props.selectable ? 1 : 0) +
    (props.singleSelectable ? 1 : 0) +
    (props.hasExpandableRows ? 1 : 0);

  return (
    <Fragment>
      <MaterialTableRow
        hover={props.clickable && !props.row.disableSelect && !props.row.disabled}
        role="row"
        aria-checked={props.isRowSelected}
        tabIndex={-1}
        key={props.row.id}
        selected={props.isRowSelected && !props.row.disableSelect && !props.row.disabled}
        data-cy="table-row"
        sx={{
          cursor: props.clickable ? 'pointer' : 'auto',
          borderBottom: props.row.heavyBottomBorder ? '2px solid #C6CCDA' : 'unset',
          backgroundColor: rowBackgroundColor,
          borderTop: props.row.heavyTopBorder ? '2px solid #c6ccda' : 'unset',
          '&.MuiTableRow-hover:hover': {
            backgroundColor: 'primary.screen',
            transition: 'background-color 0.15s ease-in-out',
            cursor: props.clickable ? 'pointer' : 'auto'
          },
          '& .MuiTypography-root, & .MuiTableCell-body': {
            color: props.row.disabled ? 'text.disabled' : 'auto',
            // note: auto is not a valid value for fill but intentionally is used to
            // invalidate the fill property so the color in the child path is used
            // MUI icons (e.g. the checkbox) keep their own disabled styling.
            '& svg:not(.MuiSvgIcon-root) path': {
              fill: props.row.disabled ? '#03a55a54' : 'auto'
            }
          },
          // Pinned cells need opaque backgrounds that track row state; selected replays MUI's overlay tint.
          '& .gcv-table-cell--pinned': {
            backgroundColor: isHighlightedRow ? rowBackgroundColor : palette.background.paper
          },
          '&.Mui-selected .gcv-table-cell--pinned': {
            backgroundImage: `linear-gradient(${alpha(palette.primary.main, 0.08)}, ${alpha(
              palette.primary.main,
              0.08
            )})`
          },
          '&.MuiTableRow-hover:hover .gcv-table-cell--pinned': {
            backgroundColor: 'primary.screen',
            backgroundImage: 'none'
          },
          ...(hasExpansion && open && { '& .MuiTableCell-root': { borderBottom: 'none' } })
        }}
      >
        {props.hasExpandableRows && (
          <MaterialTableCell
            padding="none"
            sx={{
              textAlign: 'center',
              borderBottom: (theme) =>
                props.isLastItem ? 'none' : `0.016rem solid ${theme.palette.divider}`,
              p: 0,
              width: '32px'
            }}
          >
            {hasExpansion && (
              <IconButton
                aria-label={open ? 'collapse row' : 'expand row'}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(!open);
                }}
                data-cy="expand-row"
                sx={{ p: 0, color: 'text.secondary', verticalAlign: 'middle' }}
              >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </MaterialTableCell>
        )}

        {props.selectable && (
          <TableCheckboxCell
            isItemSelected={props.isRowSelected}
            row={props.row}
            handleSelect={props.handleCheckboxSelect}
            disabled={props.row.disableSelect}
            tooltip={props.row.selectTooltip}
            isLastItem={props.isLastItem}
            hasExpandableRows={props.hasExpandableRows}
            pinned={props.pinnedLayout?.checkbox}
          />
        )}

        {props.singleSelectable && (
          <MaterialTableCell
            padding="none"
            sx={{
              textAlign: 'center',
              borderBottom: (theme) =>
                props.isLastItem ? 'none' : `0.016rem solid ${theme.palette.divider}`,
              p: 0,
              width: '35px'
            }}
          >
            <RadioButton
              checked={props.selected && !props.row.disableSelect}
              disabled={props.row.disableSelect}
              handleSelect={(value) => {
                if (!props.row.disableSelect) {
                  props.handleRadioSelect(value);
                }
              }}
              label=""
              name="tableSingleSelect"
              value={props.row.id}
            />
          </MaterialTableCell>
        )}

        {Object.values(props.columns).map((column, index) => {
          return (
            <TableCell
              key={String(column.id)}
              row={props.row}
              column={column}
              pinned={props.pinnedLayout?.cells[String(column.id)]}
              handleClick={(event: React.MouseEvent<unknown>, id: string, data: T) => {
                if (!props.row.disabled) {
                  props.handleCellClick(event, id, data);

                  if (props.handleRadioSelect) {
                    props.handleRadioSelect(props.row.id);
                  }

                  if (hasExpansion && props.clickable) {
                    setOpen((prev) => !prev);
                  }
                }
              }}
              index={index}
              isLastItem={props.isLastItem}
            />
          );
        })}
      </MaterialTableRow>
      {hasExpansion && (
        <MaterialTableRow data-cy="table-row-expansion" sx={{ backgroundColor: rowBackgroundColor }}>
          <MaterialTableCell
            colSpan={totalCols}
            sx={{
              py: 0,
              borderBottom: (theme) =>
                open && !props.isLastItem ? `0.016rem solid ${theme.palette.divider}` : 'none'
            }}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              {props.row.expandedContent}
            </Collapse>
          </MaterialTableCell>
        </MaterialTableRow>
      )}
    </Fragment>
  );
};
